import React from 'react';
import { motion } from 'framer-motion';
import { Search, Lightbulb, Paintbrush, RefreshCw, Rocket } from 'lucide-react';
import { processSteps } from '../mockData';

const iconMap = {
  Search,
  Lightbulb,
  Paintbrush,
  RefreshCw,
  Rocket
};

const Process = () => {
  return (
    <section id="process" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"></div>
      
      {/* Decorative glow */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-96 bg-gradient-to-r from-cyan-500/5 via-purple-500/5 to-blue-500/5 blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 sm:mb-20 px-4"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 mb-4">
            How ICD Builds Your Future
          </h2>
          <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-cyan-500 to-blue-600 mx-auto neon-glow"></div>
        </motion.div>

        {/* Timeline - Desktop */}
        <div className="hidden lg:block">
          {/* Connection Line */}
          <div className="relative">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, delay: 0.3 }}
              className="absolute top-10 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-600 neon-glow origin-left z-0"
            ></motion.div>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-5 gap-8 relative">
            {processSteps.map((step, index) => {
              const Icon = iconMap[step.icon];
              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="relative"
                >
                  {/* Step Number Circle */}
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.5 }}
                    className="relative z-20 mx-auto w-20 h-20 mb-6 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-2xl font-black text-white neon-glow-strong"
                    style={{ 
                      boxShadow: '0 0 0 5px #0f172a, 0 0 30px rgba(6, 182, 212, 0.6)'
                    }}
                  >
                    {step.number}
                  </motion.div>

                  {/* Icon */}
                  <div className="mb-4 flex justify-center relative z-10">
                    <div className="w-16 h-16 bg-slate-800/80 rounded-xl border border-cyan-500/30 flex items-center justify-center">
                      <Icon size={28} className="text-cyan-400" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center relative z-10">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Timeline - Mobile/Tablet */}
        <div className="lg:hidden">
          {/* Connection Line - Mobile */}
          <div className="relative mb-12">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, delay: 0.3 }}
              className="absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-600 neon-glow origin-left z-0"
            ></motion.div>
          </div>

          {/* Steps - Horizontal Scroll on Mobile */}
          <div className="relative overflow-x-auto pb-8 -mx-4 px-4">
            <div className="flex gap-4 min-w-max">
              {processSteps.map((step, index) => {
                const Icon = iconMap[step.icon];
                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.15 }}
                    className="relative w-64 flex-shrink-0"
                  >
                    {/* Step Number Circle */}
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                      className="relative z-20 mx-auto w-16 h-16 mb-6 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-xl font-black text-white neon-glow-strong shadow-lg"
                      style={{ 
                        backgroundColor: '#0f172a',
                        backgroundImage: 'linear-gradient(135deg, #06b6d4 0%, #2563eb 100%)',
                        boxShadow: '0 0 0 4px #0f172a'
                      }}
                    >
                      {step.number}
                    </motion.div>

                    {/* Icon */}
                    <div className="mb-4 flex justify-center relative z-10">
                      <div className="w-14 h-14 bg-slate-800/80 rounded-xl border border-cyan-500/30 flex items-center justify-center">
                        <Icon size={24} className="text-cyan-400" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="text-center px-2 relative z-10">
                      <h3 className="text-lg font-bold text-white mb-2">
                        {step.title}
                      </h3>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="text-center mt-4 text-cyan-400/60 text-sm flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4 animate-bounce-horizontal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>Swipe to explore our process</span>
            <svg className="w-4 h-4 animate-bounce-horizontal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Process;
